/**
 * OpenAI Tool Factory for MorphClient
 * 
 * Creates OpenAI-compatible tools that inherit the API key from MorphClient.
 */

import type { MorphClientConfig } from '../client.js';
import { createWarpGrepTool, createGitHubSearchTool } from '../tools/warp_grep/openai.js';
import { createCodebaseSearchTool } from '../tools/codebase_search/openai.js';
import { createEditFileTool } from '../tools/fastapply/openai.js';
import type { WarpGrepToolConfig, GitHubSearchToolConfig } from '../tools/warp_grep/types.js';
import type { CodebaseSearchConfig } from '../tools/codebase_search/types.js';
import type { EditFileConfig } from '../tools/fastapply/types.js';

/**
 * Factory for creating OpenAI-compatible tools with inherited API key
 * 
 * @example
 * ```typescript
 * const morph = new MorphClient({ apiKey: process.env.MORPH_API_KEY });
 * 
 * const grepTool = morph.openai.createWarpGrepTool({ repoRoot: '.' });
 * const searchTool = morph.openai.createCodebaseSearchTool({ repoId: 'my-project' });
 * const editTool = morph.openai.createEditFileTool({ baseDir: './src' });
 * 
 * // Use with OpenAI client
 * const response = await openai.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [grepTool, searchTool, editTool],
 *   messages: [{ role: 'user', content: 'Find and fix the bug' }]
 * });
 * ```
 */
export class OpenAIToolFactory {
  constructor(private config: MorphClientConfig) {}

  /**
   * Create an OpenAI-compatible warp grep tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns OpenAI ChatCompletionTool with execute and formatResult methods
   */
  createWarpGrepTool(toolConfig: Omit<WarpGrepToolConfig, 'morphApiKey'>) {
    return createWarpGrepTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create an OpenAI-compatible codebase search tool
   * 
   * @param toolConfig - Tool configuration with repoId (apiKey inherited from MorphClient)
   * @returns OpenAI ChatCompletionTool with execute and formatResult methods
   */
  createCodebaseSearchTool(toolConfig: Omit<CodebaseSearchConfig, 'apiKey'>) {
    return createCodebaseSearchTool({ 
      ...toolConfig, 
      apiKey: this.config.apiKey,
    });
  }

  /**
   * Create an OpenAI-compatible edit file tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns OpenAI ChatCompletionTool with execute and formatResult methods
   */
  createEditFileTool(toolConfig: Omit<EditFileConfig, 'morphApiKey'> = {}) {
    return createEditFileTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create an OpenAI-compatible GitHub search tool
   *
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns OpenAI ChatCompletionTool with execute and formatResult methods
   */
  createGitHubSearchTool(toolConfig?: Omit<GitHubSearchToolConfig, 'morphApiKey'>) {
    return createGitHubSearchTool({
      ...toolConfig,
      morphApiKey: this.config.apiKey,
    });
  }
}

